<template>
  <v-container fluid class="vacation-checklist-screen pa-4">
    <v-row class="mb-4 align-center no-print vacation-toolbar" dense>
      <v-col cols="12" md="auto" class="toolbar-title-col">
        <h2 class="mb-0">
          <span class="desktop-title">Urlaubs-Checkliste</span>
          <span class="mobile-title">Checkliste</span>
        </h2>

        <div class="mobile-vacation-actions no-print">
          <v-btn
            class="mobile-print-btn"
            color="primary"
            variant="tonal"
            icon="mdi-printer"
            density="comfortable"
            aria-label="Checkliste drucken"
            @click="printChecklist"
          />

          <v-menu v-if="isJoe" location="bottom end">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                class="mobile-manage-btn"
                color="secondary"
                variant="tonal"
                density="comfortable"
                prepend-icon="mdi-cog-outline"
                append-icon="mdi-chevron-down"
              >
                Verwalten
              </v-btn>
            </template>

            <v-list density="compact" min-width="260">
              <v-list-item
                prepend-icon="mdi-playlist-plus"
                title="Section hinzufügen"
                :disabled="!selectedChecklist"
                @click="openSectionCreateDialog"
              />
              <v-list-item
                prepend-icon="mdi-folder-plus-outline"
                title="Kategorie hinzufügen"
                @click="openCategoryCreateDialog"
              />
              <v-list-item
                prepend-icon="mdi-pencil-outline"
                title="Kategorie ändern"
                :disabled="!selectedChecklist"
                @click="openCategoryEditDialog"
              />
              <v-divider class="my-1" />
              <v-list-item
                prepend-icon="mdi-backup-restore"
                title="Erledigt-Haken zurücksetzen"
                :disabled="!hasCheckedItems || loading || deletingCategory || resettingChecked"
                @click="resetCategoryChecked"
              />
              <v-list-item
                prepend-icon="mdi-delete-forever-outline"
                title="Kategorie löschen"
                base-color="error"
                :disabled="!selectedChecklist || loading || deletingCategory || resettingChecked"
                @click="deleteCategoryPermanently"
              />
            </v-list>
          </v-menu>
        </div>
      </v-col>

      <v-col cols="12" md="4" class="toolbar-category-col">
        <v-select
          v-model="selectedCategoryKey"
          :items="categories"
          item-title="title"
          item-value="key"
          label="Urlaubskategorie"
          density="compact"
          hide-details
          :loading="loading"
          :disabled="resettingChecked || deletingCategory"
        />
      </v-col>

      <v-col cols="12" md="3" class="toolbar-users-col">
        <v-select
          v-model="selectedChecklistUserIds"
          :items="assignableUsers"
          item-title="name"
          item-value="id"
          label="Mitreisende"
          multiple
          chips
          closable-chips
          density="compact"
          hide-details
          :loading="savingParticipants"
          :disabled="savingParticipants"
          @update:model-value="setChecklistUsers"
        />
      </v-col>

      <v-col cols="12" md="auto" class="vacation-actions desktop-vacation-actions">
        <v-btn color="primary" prepend-icon="mdi-printer" @click="printChecklist">
          Drucken
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="isJoe" class="mb-4 no-print desktop-admin-actions" dense>
      <v-col cols="12" class="admin-actions">
        <v-btn
          color="secondary"
          variant="tonal"
          prepend-icon="mdi-playlist-plus"
          :disabled="!selectedChecklist"
          @click="openSectionCreateDialog"
        >
          Section
        </v-btn>
        <v-btn color="secondary" variant="tonal" prepend-icon="mdi-folder-plus-outline" @click="openCategoryCreateDialog">
          Kategorie
        </v-btn>
        <v-btn
          color="secondary"
          variant="tonal"
          prepend-icon="mdi-pencil-outline"
          :disabled="!selectedChecklist"
          @click="openCategoryEditDialog"
        >
          Kategorie ändern
        </v-btn>
        <v-btn
          color="secondary"
          variant="tonal"
          prepend-icon="mdi-backup-restore"
          :disabled="!hasCheckedItems || loading || deletingCategory || resettingChecked"
          :loading="resettingChecked"
          @click="resetCategoryChecked"
        >
          Erledigt-Haken zurücksetzen
        </v-btn>
        <v-btn
          color="error"
          variant="flat"
          prepend-icon="mdi-delete-forever-outline"
          :disabled="!selectedChecklist || loading || resettingChecked || deletingCategory"
          :loading="deletingCategory"
          @click="deleteCategoryPermanently"
        >
          Kategorie löschen
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4 no-print">
      {{ errorMessage }}
    </v-alert>

    <v-alert
      v-if="successMessage"
      type="success"
      variant="tonal"
      closable
      class="mb-4 no-print"
      @click:close="successMessage = ''"
    >
      {{ successMessage }}
    </v-alert>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4 no-print"
    />

    <div class="print-title">
      <h1>Urlaubs-Checkliste</h1>
      <p>{{ selectedChecklist?.title || "" }}</p>
    </div>

    <v-alert v-if="!selectedChecklist" type="warning" variant="tonal" class="mb-4 no-print">
      Keine Checkliste gefunden.
    </v-alert>

    <div v-else class="checklist-grid">
      <section
        v-for="section in selectedChecklist.sections"
        :key="section.id"
        class="checklist-section"
        :data-drag-over="dragOverSectionId === section.id ? 'true' : null"
        @dragover="handleSectionDragOver(section, $event)"
        @dragleave="handleSectionDragLeave(section, $event)"
        @drop="dropItemOnSection(section, $event)"
      >
        <header class="checklist-section-header">
          <span>{{ section.title }}</span>
          <div v-if="isJoe" class="section-header-actions no-print">
            <v-btn
              icon="mdi-pencil-outline"
              variant="text"
              density="compact"
              :aria-label="`Section ${section.title} bearbeiten`"
              @click="openSectionEditDialog(section)"
            />
            <v-btn
              icon="mdi-delete-outline"
              color="error"
              variant="text"
              density="compact"
              :disabled="deletingSection"
              :aria-label="`Section ${section.title} löschen`"
              @click="openSectionDeleteDialog(section)"
            />
          </div>
        </header>

        <div
          class="checklist-table"
          :style="tableGridStyle"
        >
          <div class="checklist-table-header" :style="tableGridStyle">
            <div class="item-title-cell"></div>
            <div class="done-title-cell">Erledigt</div>
            <div
              v-for="user in selectedChecklistUsers"
              :key="user.id"
              class="user-title-cell"
            >
              <span>{{ user.name }}</span>
            </div>
          </div>

          <div
            v-for="item in section.items"
            :key="item.id"
            class="checklist-table-row"
            :style="tableGridStyle"
            :draggable="isJoe"
            :data-dragging="draggedItem?.itemId === item.id ? 'true' : null"
            @dragstart="startItemDrag(section, item, $event)"
            @dragend="endItemDrag"
          >
            <div class="checklist-item-cell">
              <span
                class="checklist-item-text"
                :data-checked="item.checked ? 'true' : null"
                :data-shared="isItemUsedInMultipleCategories(item) ? 'true' : null"
                :data-clickable="isJoe ? 'true' : null"
                :role="isJoe ? 'button' : null"
                :tabindex="isJoe ? 0 : null"
                :title="getItemCategoryUsageTitle(item)"
                @click="openItemEditDialog(section, item)"
                @keyup.enter="openItemEditDialog(section, item)"
              >
                {{ item.text }}
              </span>
              <v-icon
                v-if="isItemUsedInMultipleCategories(item)"
                class="shared-item-icon no-print"
                size="15"
                :title="getItemCategoryUsageTitle(item)"
                aria-hidden="true"
              >mdi-link-variant</v-icon>
              <v-btn
                v-if="canDeleteItem(item)"
                class="item-quick-delete no-print"
                icon="mdi-delete-outline"
                color="error"
                variant="text"
                density="compact"
                size="x-small"
                :aria-label="`${item.text} löschen`"
                title="Item löschen"
                @click.stop="deleteItem(section.id, item.id)"
              />
            </div>

            <button
              type="button"
              class="check-box check-box--done"
              :disabled="resettingChecked"
              :aria-pressed="item.checked ? 'true' : 'false'"
              :aria-label="`${item.text}: erledigt`"
              @click="setChecked(section.id, item.id, !item.checked)"
            >
              <v-icon v-if="item.checked" size="15">mdi-check</v-icon>
            </button>

            <button
              v-for="user in selectedChecklistUsers"
              :key="`${item.id}-${user.id}`"
              type="button"
              class="check-box check-box--assignment"
              :aria-pressed="isAssigned(item, user.id) ? 'true' : 'false'"
              :aria-label="`${item.text}: ${user.name}`"
              @click="toggleAssignedUser(section.id, item, user.id)"
            >
              <v-icon v-if="isAssigned(item, user.id)" size="11">mdi-check</v-icon>
            </button>
          </div>
        </div>

        <div class="add-item-row no-print">
          <v-combobox
            v-if="isJoe"
            :model-value="getNewItemText(section.id)"
            :items="getReusableItemOptions()"
            item-title="text"
            return-object
            label="Item hinzufügen oder wiederverwenden..."
            density="compact"
            clearable
            hide-details
            @update:model-value="setNewItemText(section.id, $event)"
            @keyup.enter="addItem(section)"
          />
          <v-text-field
            v-else
            :model-value="getNewItemText(section.id)"
            label="Eigenen Punkt hinzufügen..."
            density="compact"
            hide-details
            @update:model-value="setNewItemText(section.id, $event)"
            @keyup.enter="addItem(section)"
          />
          <v-btn
            icon="mdi-plus"
            color="success"
            density="comfortable"
            :loading="isAddingItem(section.id)"
            @click="addItem(section)"
          />
        </div>
      </section>
    </div>

    <v-dialog v-model="categoryDialog" max-width="560" class="no-print">
      <v-card>
        <v-card-title>{{ categoryDialogMode === "create" ? "Kategorie hinzufügen" : "Kategorie ändern" }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="categoryForm.title" label="Titel" density="compact" />
          <v-select
            v-if="categoryDialogMode === 'create'"
            v-model="categoryForm.sourceCategoryKey"
            :items="categories"
            item-title="title"
            item-value="key"
            label="Kategorie kopieren von"
            density="compact"
            clearable
            class="mt-3"
          />
          <v-text-field v-model="categoryForm.sortOrder" label="Sortierung" type="number" density="compact" class="mt-3" />
          <v-switch v-model="categoryForm.enabled" label="Aktiv" density="compact" hide-details class="mt-3" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="categoryDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveCategory">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="sectionDialog" max-width="560" class="no-print">
      <v-card>
        <v-card-title>{{ sectionDialogMode === "create" ? "Section hinzufügen" : "Section ändern" }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="sectionForm.title" label="Titel" density="compact" />
          <v-text-field v-model="sectionForm.sortOrder" label="Sortierung" type="number" density="compact" class="mt-3" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="sectionDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveSection">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="sectionDeleteDialog" max-width="560" class="no-print">
      <v-card>
        <v-card-title>Section löschen</v-card-title>
        <v-card-text>
          <p>Section „{{ sectionToDelete?.title }}“ wirklich löschen?</p>
          <p class="mt-3">
            Alle enthaltenen Checklisteneinträge und deren Benutzerzuweisungen werden unwiderruflich gelöscht.
          </p>
          <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact" class="mt-4">
            {{ errorMessage }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="section-delete-actions">
          <v-spacer />
          <v-btn variant="text" :disabled="deletingSection" @click="closeSectionDeleteDialog">
            Abbrechen
          </v-btn>
          <v-btn color="error" variant="flat" :loading="deletingSection" @click="deleteSection">
            Section löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="itemDialog" max-width="560" class="no-print">
      <v-card>
        <v-card-title>Item ändern</v-card-title>
        <v-card-text>
          <v-text-field v-model="itemForm.text" label="Name" density="compact" />
          <v-text-field v-model="itemForm.sortOrder" label="Sortierung" type="number" density="compact" class="mt-3" />
          <v-select
            v-model="itemForm.targetSectionId"
            :items="editTargetSections"
            item-title="title"
            item-value="id"
            label="In Section verschieben"
            density="compact"
            clearable
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions class="item-dialog-actions">
          <v-btn
            v-if="canDeleteEditedItem"
            class="item-dialog-delete"
            color="error"
            variant="text"
            @click="deleteEditedItem"
          >
            Löschen
          </v-btn>
          <v-spacer class="item-dialog-spacer" />
          <v-btn class="item-dialog-cancel" variant="text" @click="itemDialog = false">Abbrechen</v-btn>
          <v-btn class="item-dialog-save" color="primary" :loading="saving" @click="saveItem">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

const store = useStore();

const categories = computed(() => store.getters.stateVacationChecklistCategories || []);
const assignableUsers = computed(() => store.getters.stateVacationAssignableUsers || []);
const loading = computed(() => store.getters.stateVacationChecklistLoading);
const saving = computed(() => store.getters.stateVacationChecklistSaving);
const error = computed(() => store.getters.stateVacationChecklistError);
const currentUser = computed(() => store.getters.stateUser || {});
const isJoe = computed(() => Number(currentUser.value?.iduser) === 1);
const selectedCategoryKey = ref("");
const selectedChecklistUserIds = ref([]);
const newItemTextBySection = ref({});
const addingSectionIds = ref(new Set());
const categoryDialog = ref(false);
const categoryDialogMode = ref("create");
const categoryForm = ref(defaultCategoryForm());
const sectionDialog = ref(false);
const sectionDialogMode = ref("create");
const sectionForm = ref(defaultSectionForm());
const sectionDeleteDialog = ref(false);
const sectionToDelete = ref(null);
const successMessage = ref("");
const resettingChecked = ref(false);
const deletingCategory = ref(false);
const deletingSection = ref(false);
const savingParticipants = ref(false);
const itemDialog = ref(false);
const itemForm = ref(defaultItemForm());
const draggedItem = ref(null);
const dragOverSectionId = ref("");
const selectedChecklist = computed(() =>
  store.getters.getVacationChecklistByKey(selectedCategoryKey.value)
);
const hasCheckedItems = computed(() =>
  (selectedChecklist.value?.sections || []).some((section) =>
    (section.items || []).some((item) => item.checked)
  )
);
let initializing = false;
let syncingParticipantsFromChecklist = false;
const selectedChecklistUsers = computed(() => {
  const selectedIds = new Set(selectedChecklistUserIds.value);
  return assignableUsers.value.filter((user) => selectedIds.has(user.id));
});
const editTargetSections = computed(() => {
  const currentSectionId = itemForm.value.sectionId;
  return (selectedChecklist.value?.sections || []).filter((section) => section.id !== currentSectionId);
});
const canDeleteEditedItem = computed(() => canDeleteItem(findItemById(itemForm.value.sectionId, itemForm.value.itemId)));
const errorMessage = computed(() => formatError(error.value));
const itemCategoryUsages = computed(() => {
  const categoriesByReusableKey = new Map();
  const checklists = Object.entries(store.getters.stateVacationChecklists || {});

  for (const [categoryKey, checklist] of checklists) {
    const categoryTitle = checklist?.title
      || categories.value.find((category) => category.key === categoryKey)?.title
      || categoryKey;
    for (const section of checklist?.sections || []) {
      for (const item of section.items || []) {
        const reusableKey = item.reusable_item_key;
        if (!reusableKey) continue;
        if (!categoriesByReusableKey.has(reusableKey)) {
          categoriesByReusableKey.set(reusableKey, new Map());
        }
        categoriesByReusableKey.get(reusableKey).set(categoryKey, categoryTitle);
      }
    }
  }

  return new Map(
    Array.from(categoriesByReusableKey, ([reusableKey, categoryMap]) => [
      reusableKey,
      Array.from(categoryMap.values()),
    ])
  );
});
const tableGridStyle = computed(() => {
  const userColumnCount = selectedChecklistUsers.value.length;
  const userColumns = userColumnCount ? ` repeat(${userColumnCount}, 36px)` : "";
  return {
    gridTemplateColumns: `minmax(150px, 1fr) 64px${userColumns}`,
    minWidth: `${214 + userColumnCount * 36}px`,
  };
});

onMounted(async () => {
  initializing = true;
  try {
    const initialLoadActions = [
      store.dispatch("loadVacationChecklistCategories"),
      store.dispatch("loadVacationChecklistAssignableUsers"),
    ];
    if (isJoe.value) {
      initialLoadActions.push(store.dispatch("loadVacationChecklists"));
    }
    await Promise.all(initialLoadActions);

    if (!selectedCategoryKey.value) {
      selectedCategoryKey.value = categories.value[0]?.key || "tent_car";
    }

    if (selectedCategoryKey.value) {
      await store.dispatch("loadVacationChecklist", selectedCategoryKey.value);
    }
  } finally {
    initializing = false;
  }
});

watch(selectedCategoryKey, (categoryKey) => {
  if (categoryKey && !initializing) {
    store.dispatch("loadVacationChecklist", categoryKey);
  }
});

watch(
  () => selectedChecklist.value?.participant_user_ids,
  (participantUserIds) => {
    syncingParticipantsFromChecklist = true;
    selectedChecklistUserIds.value = normalizeUserIds(participantUserIds);
    syncingParticipantsFromChecklist = false;
  },
  { immediate: true }
);

function setChecked(sectionId, itemId, checked) {
  store.dispatch("updateVacationChecklistItemChecked", {
    categoryKey: selectedCategoryKey.value,
    itemId,
    checked,
  });
}

function setAssignedUsers(sectionId, itemId, assignedUserIds) {
  store.dispatch("updateVacationChecklistItemUsers", {
    categoryKey: selectedCategoryKey.value,
    itemId,
    assignedUserIds: Array.isArray(assignedUserIds)
      ? assignedUserIds.slice(0, 5)
      : [],
  });
}

async function setChecklistUsers(userIds) {
  if (savingParticipants.value) return;
  const normalizedUserIds = normalizeUserIds(userIds);
  selectedChecklistUserIds.value = normalizedUserIds;

  if (syncingParticipantsFromChecklist || !selectedCategoryKey.value) return;

  savingParticipants.value = true;
  try {
    await store.dispatch("setVacationChecklistParticipants", {
      categoryKey: selectedCategoryKey.value,
      participantUserIds: normalizedUserIds,
    });
  } catch (_) {
    // Die konkrete API-Fehlermeldung wird über errorMessage angezeigt.
  } finally {
    savingParticipants.value = false;
  }
}

function isAssigned(item, userId) {
  return Array.isArray(item?.assigned_user_ids) && item.assigned_user_ids.includes(userId);
}

function getItemCategoryUsageCount(item) {
  return getItemCategoryUsages(item).length;
}

function getItemCategoryUsages(item) {
  return itemCategoryUsages.value.get(item?.reusable_item_key) || [];
}

function isItemUsedInMultipleCategories(item) {
  return getItemCategoryUsageCount(item) > 1;
}

function getItemCategoryUsageTitle(item) {
  const categoryTitles = getItemCategoryUsages(item);
  return categoryTitles.length > 1
    ? `Verwendet in: ${categoryTitles.join(", ")}`
    : null;
}

function toggleAssignedUser(sectionId, item, userId) {
  const current = Array.isArray(item?.assigned_user_ids) ? item.assigned_user_ids : [];
  const next = current.includes(userId)
    ? current.filter((id) => id !== userId)
    : [...current, userId];

  setAssignedUsers(sectionId, item.id, next);
}

function setNewItemText(sectionId, text) {
  newItemTextBySection.value = {
    ...newItemTextBySection.value,
    [newItemInputKey(sectionId)]: text,
  };
}

async function addItem(section) {
  const itemValue = getNewItemText(section?.id);
  const selectedReusableItem = typeof itemValue === "object" && itemValue !== null ? itemValue : null;
  const text = String(selectedReusableItem ? selectedReusableItem.text : itemValue || "").trim();
  if (!text && !selectedReusableItem?.id) return;

  setAdding(section.id, true);
  try {
    await store.dispatch("addVacationChecklistItem", {
      categoryKey: selectedCategoryKey.value,
      sectionId: section.id,
      text,
      sourceItemId: selectedReusableItem?.id,
    });
    setNewItemText(section.id, "");
  } finally {
    setAdding(section.id, false);
  }
}

function canDeleteItem(item) {
  return isJoe.value || item?.custom;
}

function deleteItem(sectionId, itemId) {
  const item = findItemById(sectionId, itemId);
  if (!item) return;

  if (!item.custom && !isJoe.value) return;

  if (!item.custom) {
    const confirmed = window.confirm(`Item "${item.text}" dauerhaft loeschen?`);
    if (!confirmed) return;
  }

  store.dispatch("deleteVacationChecklistItem", {
    categoryKey: selectedCategoryKey.value,
    itemId,
  });
}

function openCategoryCreateDialog() {
  categoryDialogMode.value = "create";
  categoryForm.value = defaultCategoryForm();
  categoryDialog.value = true;
}

function openCategoryEditDialog() {
  if (!selectedChecklist.value) return;
  const category = categories.value.find((item) => item.key === selectedCategoryKey.value);
  categoryDialogMode.value = "edit";
  categoryForm.value = {
    key: selectedChecklist.value.key,
    title: selectedChecklist.value.title || "",
    sortOrder: category?.sort_order ?? "",
    enabled: category?.enabled !== false,
  };
  categoryDialog.value = true;
}

async function saveCategory() {
  const previousCategoryKey = selectedCategoryKey.value;
  if (categoryDialogMode.value === "create") {
    const created = await store.dispatch("createVacationChecklistCategory", {
      title: categoryForm.value.title,
      sortOrder: categoryForm.value.sortOrder,
      enabled: categoryForm.value.enabled,
      sourceCategoryKey: categoryForm.value.sourceCategoryKey,
    });
    selectedCategoryKey.value = created?.key || "";
  } else {
    await store.dispatch("updateVacationChecklistCategory", {
      categoryKey: selectedCategoryKey.value,
      patch: {
        title: categoryForm.value.title,
        sortOrder: categoryForm.value.sortOrder,
        enabled: categoryForm.value.enabled,
      },
    });
  }
  categoryDialog.value = false;
  if (categoryDialogMode.value === "edit" && categoryForm.value.enabled === false) {
    selectNextAvailableCategory(previousCategoryKey);
  }
}

async function resetCategoryChecked() {
  if (!isJoe.value || !hasCheckedItems.value || loading.value || saving.value || resettingChecked.value || deletingCategory.value) return;

  const categoryKey = selectedCategoryKey.value;
  const title = selectedChecklist.value.title || categoryKey;
  resettingChecked.value = true;
  successMessage.value = "";
  try {
    await store.dispatch("resetVacationChecklist", categoryKey);
    successMessage.value = `Alle Erledigt-Haken in „${title}“ wurden zurückgesetzt.`;
  } catch (_) {
    // Die konkrete API-Fehlermeldung wird über errorMessage angezeigt.
  } finally {
    resettingChecked.value = false;
  }
}

async function deleteCategoryPermanently() {
  if (!selectedChecklist.value || !isJoe.value || loading.value || saving.value || resettingChecked.value || deletingCategory.value) return;

  const categoryKey = selectedCategoryKey.value;
  const title = selectedChecklist.value.title || categoryKey;
  const confirmed = window.confirm(
    `Kategorie "${title}" dauerhaft loeschen?\n\nDabei werden alle Sections, Items, Teilnehmer und Zuweisungen dieser Kategorie geloescht.`
  );
  if (!confirmed) return;

  deletingCategory.value = true;
  successMessage.value = "";
  try {
    await store.dispatch("deleteVacationChecklistCategory", categoryKey);
    selectNextAvailableCategory(categoryKey);
  } catch (_) {
    // Die konkrete API-Fehlermeldung wird über errorMessage angezeigt.
  } finally {
    deletingCategory.value = false;
  }
}

function openSectionCreateDialog() {
  sectionDialogMode.value = "create";
  sectionForm.value = defaultSectionForm();
  sectionDialog.value = true;
}

function openSectionEditDialog(section) {
  sectionDialogMode.value = "edit";
  sectionForm.value = {
    key: section.id,
    title: section.title || "",
    sortOrder: section.sort_order ?? "",
  };
  sectionDialog.value = true;
}

function openSectionDeleteDialog(section) {
  if (!isJoe.value || !section?.id) return;
  successMessage.value = "";
  sectionToDelete.value = {
    id: section.id,
    title: section.title || section.id,
  };
  sectionDeleteDialog.value = true;
}

function closeSectionDeleteDialog() {
  if (deletingSection.value) return;
  sectionDeleteDialog.value = false;
  sectionToDelete.value = null;
}

async function deleteSection() {
  const section = sectionToDelete.value;
  if (!isJoe.value || !selectedCategoryKey.value || !section?.id || saving.value || deletingSection.value) return;

  deletingSection.value = true;
  try {
    await store.dispatch("deleteVacationChecklistSection", {
      categoryKey: selectedCategoryKey.value,
      sectionKey: section.id,
    });
    sectionDeleteDialog.value = false;
    sectionToDelete.value = null;
    successMessage.value = `Section „${section.title}“ wurde gelöscht.`;
  } catch (_) {
    // Die konkrete API-Fehlermeldung wird über errorMessage angezeigt.
  } finally {
    deletingSection.value = false;
  }
}

async function saveSection() {
  if (sectionDialogMode.value === "create") {
    await store.dispatch("createVacationChecklistSection", {
      categoryKey: selectedCategoryKey.value,
      title: sectionForm.value.title,
      sortOrder: sectionForm.value.sortOrder,
    });
  } else {
    await store.dispatch("updateVacationChecklistSection", {
      categoryKey: selectedCategoryKey.value,
      sectionKey: sectionForm.value.key,
      patch: {
        title: sectionForm.value.title,
        sortOrder: sectionForm.value.sortOrder,
      },
    });
  }
  sectionDialog.value = false;
}

function openItemEditDialog(section, item) {
  if (!isJoe.value) return;

  itemForm.value = {
    sectionId: section.id,
    itemId: item.id,
    text: item.text || "",
    sortOrder: item.sort_order ?? "",
    targetSectionId: "",
  };
  itemDialog.value = true;
}

async function saveItem() {
  const text = String(itemForm.value.text || "").trim();
  if (!text) return;

  await store.dispatch("updateVacationChecklistItem", {
    categoryKey: selectedCategoryKey.value,
    itemId: itemForm.value.itemId,
    patch: {
      text,
      sortOrder: itemForm.value.sortOrder,
    },
  });

  if (itemForm.value.targetSectionId) {
    await moveItemToSection(itemForm.value.itemId, itemForm.value.targetSectionId);
  }

  itemDialog.value = false;
}

async function deleteEditedItem() {
  const sectionId = itemForm.value.sectionId;
  const itemId = itemForm.value.itemId;
  if (!canDeleteItem(findItemById(sectionId, itemId))) return;

  await deleteItem(sectionId, itemId);
  itemDialog.value = false;
}

function startItemDrag(section, item, event) {
  if (!isJoe.value || !section?.id || !item?.id) return;

  draggedItem.value = {
    sourceSectionId: section.id,
    itemId: item.id,
  };
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(item.id));
  }
}

function endItemDrag() {
  draggedItem.value = null;
  dragOverSectionId.value = "";
}

function handleSectionDragOver(section, event) {
  if (!canDropItemOnSection(section)) return;

  event.preventDefault();
  dragOverSectionId.value = section.id;
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "move";
  }
}

function handleSectionDragLeave(section, event) {
  if (event.currentTarget?.contains(event.relatedTarget)) return;
  if (dragOverSectionId.value === section.id) {
    dragOverSectionId.value = "";
  }
}

async function dropItemOnSection(section, event) {
  if (!canDropItemOnSection(section)) return;

  event.preventDefault();
  const itemId = draggedItem.value.itemId;
  dragOverSectionId.value = "";
  await moveItemToSection(itemId, section.id);
  draggedItem.value = null;
}

function canDropItemOnSection(section) {
  return Boolean(
    isJoe.value &&
    section?.id &&
    draggedItem.value?.itemId &&
    draggedItem.value.sourceSectionId !== section.id
  );
}

function moveItemToSection(itemId, sectionKey) {
  return store.dispatch("moveVacationChecklistItem", {
    categoryKey: selectedCategoryKey.value,
    itemId,
    sectionKey,
  });
}

function printChecklist() {
  window.print();
}

function newItemInputKey(sectionId) {
  return `${selectedCategoryKey.value}:${sectionId}`;
}

function getNewItemText(sectionId) {
  return newItemTextBySection.value[newItemInputKey(sectionId)] || "";
}

function getReusableItemOptions() {
  const existingKeys = new Set(getSelectedChecklistReusableKeys());
  return getAllReusableItems()
    .filter((item) => !existingKeys.has(item.reusable_item_key))
    .sort((a, b) => a.text.localeCompare(b.text));
}

function getSelectedChecklistReusableKeys() {
  return (selectedChecklist.value?.sections || [])
    .flatMap((section) => section.items || [])
    .map((item) => item.reusable_item_key)
    .filter(Boolean);
}

function getAllReusableItems() {
  const itemsByKey = new Map();
  const checklists = Object.values(store.getters.stateVacationChecklists || {});

  for (const checklist of checklists) {
    for (const section of checklist?.sections || []) {
      for (const item of section.items || []) {
        const reusableKey = item.reusable_item_key || String(item.id);
        if (!itemsByKey.has(reusableKey)) {
          itemsByKey.set(reusableKey, {
            id: item.id,
            reusable_item_key: reusableKey,
            text: item.text,
          });
        }
      }
    }
  }

  return Array.from(itemsByKey.values()).filter((item) => item.id && item.text);
}

function setAdding(sectionId, value) {
  const next = new Set(addingSectionIds.value);
  if (value) {
    next.add(sectionId);
  } else {
    next.delete(sectionId);
  }
  addingSectionIds.value = next;
}

function isAddingItem(sectionId) {
  return addingSectionIds.value.has(sectionId);
}

function findItemById(sectionId, itemId) {
  const section = (selectedChecklist.value?.sections || []).find((entry) => entry.id === sectionId);
  return (section?.items || []).find((entry) => entry.id === itemId) || null;
}

function formatError(value) {
  const detail = value?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((entry) => entry?.msg || entry?.message || JSON.stringify(entry))
      .join("; ");
  }
  return value?.message || "";
}

function defaultCategoryForm() {
  return {
    title: "",
    sourceCategoryKey: "",
    sortOrder: "",
    enabled: true,
  };
}

function defaultSectionForm() {
  return {
    key: "",
    title: "",
    sortOrder: "",
  };
}

function defaultItemForm() {
  return {
    sectionId: "",
    itemId: "",
    text: "",
    sortOrder: "",
    targetSectionId: "",
  };
}

function normalizeUserIds(userIds) {
  return Array.from(new Set(Array.isArray(userIds) ? userIds : []))
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
    .slice(0, 5);
}

function selectNextAvailableCategory(previousCategoryKey) {
  const nextCategory = categories.value.find((category) => category.key !== previousCategoryKey) || categories.value[0] || null;
  selectedCategoryKey.value = nextCategory?.key || "";
  if (!selectedCategoryKey.value) return;
  store.dispatch("loadVacationChecklist", selectedCategoryKey.value);
}

</script>

<style scoped>
.vacation-checklist-screen {
  min-height: calc(100vh - 64px);
}

.vacation-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.admin-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.mobile-vacation-actions {
  display: none;
}

.mobile-title {
  display: none;
}

.print-title {
  display: none;
}

.vacation-toolbar {
  gap: 10px;
}

.toolbar-title-col,
.toolbar-category-col,
.toolbar-users-col,
.vacation-actions {
  flex: 0 0 auto;
}

.toolbar-title-col {
  min-width: 260px;
}

.toolbar-category-col {
  width: min(420px, 100%);
}

.toolbar-users-col {
  width: min(300px, 100%);
}

.checklist-grid {
  columns: 1;
  column-gap: 14px;
}

.checklist-section {
  background: #1f1f1f;
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  color: #f5f5f5;
  display: inline-block;
  margin: 0 0 14px;
  min-width: 0;
  padding: 12px 14px 14px;
  width: 100%;
  break-inside: avoid;
}

.checklist-section[data-drag-over="true"] {
  border-color: #42a5f5;
  box-shadow: inset 0 0 0 1px rgba(66, 165, 245, 0.55);
}

.checklist-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #42a5f5;
  font-size: 1rem;
  font-weight: 800;
  margin-bottom: 8px;
}

.section-header-actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 2px;
}

.checklist-table {
  width: 100%;
  overflow-x: hidden;
}

.checklist-table-header,
.checklist-table-row {
  display: grid;
  align-items: center;
}

.checklist-table-header {
  border-bottom: 1px solid #3a3a3a;
  min-height: 46px;
}

.checklist-table-row {
  border-bottom: 1px solid #444;
  min-height: 36px;
}

.checklist-table-row[draggable="true"] {
  cursor: grab;
}

.checklist-table-row[data-dragging="true"] {
  opacity: 0.45;
}

.checklist-item-text {
  padding: 6px 8px 6px 0;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.checklist-item-cell {
  display: flex;
  align-items: center;
  min-width: 0;
}

.checklist-item-cell .checklist-item-text {
  min-width: 0;
}

.checklist-item-text[data-checked="true"] {
  color: #64748b;
  text-decoration: line-through;
}

.checklist-item-text[data-clickable="true"] {
  cursor: pointer;
}

.checklist-item-text[data-shared="true"] {
  text-decoration-line: underline;
  text-decoration-style: dotted;
  text-decoration-color: rgba(144, 202, 249, 0.55);
  text-underline-offset: 3px;
}

.checklist-item-text[data-shared="true"][data-checked="true"] {
  text-decoration-line: line-through underline;
}

.shared-item-icon {
  flex: 0 0 auto;
  margin-left: 2px;
  color: #90a4ae;
  opacity: 0.72;
}

.item-quick-delete {
  flex: 0 0 auto;
  margin-left: 1px;
  opacity: 0.68;
}

.item-quick-delete:hover,
.item-quick-delete:focus-visible {
  opacity: 1;
}

.done-title-cell,
.user-title-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.user-title-cell {
  align-self: stretch;
  border-left: 1px solid #444;
  font-weight: 700;
}

.done-title-cell {
  border-left: 1px solid #444;
  font-weight: 700;
}

.user-title-cell span {
  display: inline-block;
  white-space: nowrap;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  max-height: 76px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.check-box {
  align-self: center;
  justify-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 2px solid #d0d0d0;
  background: transparent;
  color: #42a5f5;
  cursor: pointer;
  padding: 0;
}

.check-box[aria-pressed="true"] {
  background: rgba(66, 165, 245, 0.14);
  border-color: #42a5f5;
}

.check-box--assignment {
  width: 16px;
  height: 16px;
  border-width: 1px;
  border-color: #90a4ae;
  color: #b0bec5;
}

.check-box--assignment[aria-pressed="true"] {
  background: rgba(144, 164, 174, 0.18);
  border-color: #b0bec5;
}

.add-item-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 42px;
  gap: 10px;
  margin-top: 12px;
}

@media (min-width: 1100px) {
  .checklist-grid {
    columns: 2 480px;
  }
}

@media (min-width: 1600px) {
  .checklist-grid {
    columns: 3 420px;
  }
}

@media (max-width: 960px) {
  .vacation-actions {
    justify-content: flex-start;
  }

  .checklist-table {
    overflow-x: auto;
  }
}

@media (max-width: 600px) {
  .desktop-vacation-actions,
  .desktop-admin-actions {
    display: none;
  }

  .toolbar-title-col {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
    width: 100%;
  }

  .toolbar-title-col h2 {
    flex: 0 1 auto;
    font-size: 1.25rem;
    line-height: 1.2;
    white-space: nowrap;
  }

  .desktop-title {
    display: none;
  }

  .mobile-title {
    display: inline;
  }

  .mobile-vacation-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
  }

  .mobile-print-btn,
  .mobile-manage-btn {
    min-width: 0;
  }

  .item-dialog-actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 4px 8px;
    padding: 8px 12px 12px;
  }

  .item-dialog-spacer {
    display: none;
  }

  .item-dialog-delete {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .item-dialog-cancel,
  .item-dialog-save {
    width: 100%;
    margin: 0 !important;
  }
}

@media print {
  :global(.v-app-bar),
  :global(.v-navigation-drawer),
  :global(.v-footer),
  :global(#nav),
  :global(header),
  :global(.navbar),
  :global(.navbar-nav),
  :global(.navbar-collapse),
  :global(.v-overlay-container),
  .no-print {
    display: none !important;
  }

  :global(.v-main),
  :global(.v-main__wrap),
  :global(.v-container) {
    padding: 0 !important;
  }

  .vacation-checklist-screen {
    background: #fff;
    color: #000;
    min-height: auto;
    padding: 0 !important;
  }

  .print-title {
    display: block;
    margin-bottom: 5mm;
  }

  .print-title h1 {
    font-size: 15pt;
    margin: 0;
  }

  .print-title p {
    font-size: 9pt;
    margin: 1mm 0 0;
  }

  .checklist-grid {
    columns: 2;
    column-gap: 4mm;
  }

  .checklist-section {
    background: #fff;
    border: 1px solid #999;
    border-radius: 0;
    color: #000;
    display: inline-block;
    margin: 0 0 4mm;
    padding: 3mm;
    width: 100%;
    min-height: 0;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .checklist-section-header {
    color: #000;
    font-size: 9.5pt;
    margin-bottom: 1.5mm;
  }

  .checklist-table-header,
  .checklist-table-row {
    grid-template-columns: minmax(44mm, 1fr) 7mm repeat(5, 7mm);
    min-width: 0;
  }

  .checklist-table-header {
    min-height: 12mm;
  }

  .checklist-table-row {
    min-height: 5.6mm;
    font-size: 7.6pt;
  }

  .checklist-item-text {
    padding: 1mm 1.5mm 1mm 0;
    line-height: 1.12;
  }

  .check-box {
    width: 4.4mm;
    height: 4.4mm;
    color: #000;
    border-color: #000;
    border-width: 1px;
  }

  .check-box--assignment {
    width: 3.2mm;
    height: 3.2mm;
    border-color: #555;
    border-width: 0.6px;
  }

  .check-box--assignment[aria-pressed="true"] {
    background: #fff;
    border-color: #000;
  }

  .done-title-cell,
  .user-title-cell {
    font-size: 7pt;
  }

  .user-title-cell span {
    max-height: 42px;
  }

  .user-title-cell,
  .checklist-table-row {
    border-color: #aaa;
  }

  .checklist-item-text[data-checked="true"] {
    color: #000;
    text-decoration: none;
  }
}
</style>
